import type { Building } from "../api/types";
import { fmt, pct } from "../lib/format";
import { milestoneCopy } from "../lib/milestone";
import { Link } from "../router";
import { Elevation } from "./Elevation";
import { useScoring } from "../hooks/useScoring";
import { BuildingScore } from "./ScoreBreakdown";

interface Props {
  rows: Building[];
  onSelect: (building: Building) => void;
  /**
   * Whether these rows carry the parcel folio. Preview is served them without
   * it — stripped in the response, not here — so this only decides whether
   * there is a folio to print.
   */
  canSeeFolio: boolean;
}

export function BuildingsTable({ rows, onSelect, canSeeFolio }: Props) {
  const rules = useScoring();
  return (
    <table>
      <thead>
        <tr>
          <th style={{ width: 26 }}>
            <span className="visually-hidden">Elevation</span>
          </th>
          <th>Building</th>
          <th className="num">Units</th>
          <th className="num">Affected</th>
          <th className="hide-sm">Signals</th>
          <th className="num">
            <Link to="/methodology" className="th-link">Score</Link>
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((building, index) => (
          <tr
            key={building.parent_folio}
            className="clickable"
            tabIndex={0}
            onClick={() => onSelect(building)}
            onKeyDown={(event) => {
              // A clickable row must also be reachable from the keyboard.
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(building);
              }
            }}
          >
            <td>
              <Elevation
                floors={building.derived_floors}
                rate={building.distress_rate}
                unsafe={building.unsafe_structure_cases > 0}
                index={index}
              />
            </td>
            <td>
              <div className="addr">{building.address ?? "Address unknown"}</div>
              <div className="sub">
                {building.year_built ?? "—"} ·{" "}
                {building.derived_floors
                  ? `${building.derived_floors} fl`
                  : "floors unknown"}
                {canSeeFolio ? ` · ${building.parent_folio}` : ""}
              </div>
            </td>
            <td className="num mono">{fmt(building.total_units)}</td>
            <td className="num mono">
              {building.distressed_units}
              <span style={{ color: "var(--concrete)" }}>
                {" "}
                · {pct(building.distress_rate)}
              </span>
            </td>
            <td className="hide-sm">
              <Signals building={building} />
            </td>
            <td className="num">
              <BuildingScore building={building} rules={rules} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Signals({ building }: { building: Building }) {
  // Badge text differs by provenance: "enforcement" is the county saying so,
  // "est. overdue" is our inference. They must never read the same.
  const milestone = milestoneCopy(building);
  return (
    <>
      {building.unsafe_structure_cases > 0 && (
        <span className="badge b-red">
          {building.unsafe_structure_cases} unsafe
        </span>
      )}
      {building.lien_cases > 0 && (
        <span className="badge b-red">{building.lien_cases} lien</span>
      )}
      {building.expired_permit_cases > 0 && (
        <span className="badge b-amber">
          {building.expired_permit_cases} expired
        </span>
      )}
      {milestone.badge && milestone.badgeTone && (
        <span className={`badge ${milestone.badgeTone}`} title={milestone.basis}>
          {milestone.badge}
        </span>
      )}
    </>
  );
}
